import { useState, useEffect, useCallback } from 'react';
import { useAuthFetch } from './useAuthFetch';
import { useAuth } from '../contexts/AuthContext';

export interface UsageData {
  plan: string;
  planDisplayName: string;
  billingCycleStart: string;
  usage: {
    resumeUploads: {
      used: number;
      monthly: number;
      stored: number;
      maxStored: number;
    };
    agentSessions?: {
      used: number;
      monthly: number;
      dailyUsed?: number;
      dailyCap?: number;
    };
    activeJobs?: {
      current: number;
      limit: number;
    };
    seats?: {
      current: number;
      limit: number;
    };
    pages?: {
      processed: number;
    };
  };
  features: Record<string, boolean>;
  fileSize: {
    maxBytes: number;
  };
  activeJobs?: number;
  seats?: number;
}

/**
 * Hook to fetch and cache user usage data.
 * Auto-refreshes after metered actions.
 */
export const useUsage = () => {
  const { user } = useAuth();
  const authFetch = useAuthFetch();
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsage = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch('/api/usage');
      if (response.ok) {
        const data = await response.json();
        setUsage(data);
      } else {
        setError(`Failed to load usage (${response.status})`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch usage');
      console.error('Failed to fetch usage:', err);
    } finally {
      setLoading(false);
    }
  }, [user, authFetch]);

  useEffect(() => {
    fetchUsage();
    const interval = setInterval(fetchUsage, 30_000);
    const onInvalidate = () => fetchUsage();
    window.addEventListener('usage:invalidate', onInvalidate);
    return () => {
      clearInterval(interval);
      window.removeEventListener('usage:invalidate', onInvalidate);
    };
  }, [fetchUsage]);

  return { usage, loading, error, refreshUsage: fetchUsage };
};
